import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { ContentTabs } from '../../../core/components/ContentTabs';
import { Files } from '../../../core/components/Files';
import UploadSection from '../../../core/components/UploadSection';
import {
  TaskOccurrence,
  taskTemplatesApi,
  useAddOccurrenceNoteMutation,
  useArchiveOccurrenceFileMutation,
  useDeleteOccurrenceNoteMutation,
  useGetOccurrenceFilesQuery,
  useGetOccurrenceHistoryQuery,
  useGetOccurrenceNotesQuery,
  useGetTaskTemplateMediaQuery,
  useGetTaskTemplateQuery,
} from '../../../core/api/rtk/taskTemplatesApi';
import { postTaskOccurrenceFile } from '../../../core/api/files';
import TaskTemplateLink from './TaskTemplateLink';
import { apiError } from '../../../core/toastMessages';
import { selectRole, selectUserGroup } from '../../../store/authReducer';
import { hasRole, RoleTypes } from '../../../core/components/Permission';
import {
  findPermissionRef,
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';

export interface OccurrenceDrawerBodyProps {
  occurrence: TaskOccurrence;
  onClose: () => void;
  onSave: (patch: Partial<TaskOccurrence>) => Promise<void> | void;
  isSaving: boolean;
  error: string | null;
}

const stripHtml = (value?: string | null): string => {
  if (!value) return '';
  return String(value)
    .replace(/<[^>]*>/g, '')
    .trim();
};

const normalizeUrl = (raw: string): string => {
  if (!raw) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

export const OccurrenceDrawerBody = ({
  occurrence,
  onClose,
  onSave,
  isSaving,
  error,
}: OccurrenceDrawerBodyProps): JSX.Element => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const selectedSiteId = useSelector((state: any) => state?.auth?.selectedSite ?? null) as
    | number
    | null;
  const role = useSelector(selectRole);
  const userGroup = useSelector(selectUserGroup);
  const enhancedRole = findPermissionRef(
    (userGroup as any)?.permissions ?? [],
    viewPermissionRefs.admin
  )
    ? 'Admin'
    : role;
  const toLocal = (iso?: string | null): string =>
    iso ? moment(iso).format('YYYY-MM-DDTHH:mm') : '';
  const [notes, setNotes] = useState<string>(occurrence.notes || '');
  const [scheduleStart, setScheduleStart] = useState<string>(toLocal(occurrence.scheduleStart));
  const [scheduleEnd, setScheduleEnd] = useState<string>(toLocal(occurrence.scheduleEnd));

  // List endpoint doesn't graph-fetch linkedTaskTemplates / description / videos.
  const parentTaskId = occurrence.taskId;
  const { data: parentTask, isFetching: parentLoading } = useGetTaskTemplateQuery(
    parentTaskId as number,
    { skip: !parentTaskId }
  );
  const hasLinks = (parentTask?.linkedTaskTemplates ?? []).some((link) => !link.archivedAt);

  const handleSave = (): void => {
    onSave({
      notes,
      scheduleStart: scheduleStart ? moment(scheduleStart).toISOString() : null,
      scheduleEnd: scheduleEnd ? moment(scheduleEnd).toISOString() : null,
    });
  };

  const detailsTab = (
    <div>
      <div className="form-group">
        <label>{intl.formatMessage({ id: 'status' })}</label>
        <input className="form-control" value={occurrence.status} disabled readOnly />
      </div>
      <div className="form-group">
        <label>{intl.formatMessage({ id: 'startsAt', defaultMessage: 'Starts at' })}</label>
        <input
          type="datetime-local"
          className="form-control"
          value={scheduleStart}
          onChange={(e) => setScheduleStart(e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>{intl.formatMessage({ id: 'endsAt', defaultMessage: 'Ends at' })}</label>
        <input
          type="datetime-local"
          className="form-control"
          value={scheduleEnd}
          onChange={(e) => setScheduleEnd(e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>{intl.formatMessage({ id: 'notes' })}</label>
        <textarea
          className="form-control"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </div>
  );

  // Read-through of TaskTemplate media; admins manage uploads via TasksV2Admin.
  const { data: templateMedia = [] } = useGetTaskTemplateMediaQuery(parentTaskId as number, {
    skip: !parentTaskId,
  });
  const isMasterTemplate =
    (parentTask as any)?.siteId == null && (parentTask as any)?.groupId != null;
  const requiredRole = isMasterTemplate ? RoleTypes.OperatorGroupAdmin : RoleTypes.SiteManager;
  const canModifyFiles = hasRole(enhancedRole, requiredRole) && !!selectedSiteId;

  const occurrenceVideos: string[] | null = (occurrence as any).videos ?? null;
  const templateVideos = parentTask?.videos ?? null;
  const externalVideos = useMemo(
    () =>
      (occurrenceVideos ?? templateVideos ?? []).filter(
        (v): v is string => typeof v === 'string' && !!v
      ),
    [occurrenceVideos, templateVideos]
  );

  const mediaSection = (
    <div className="form-group">
      <label>{intl.formatMessage({ id: 'media', defaultMessage: 'Media' })}</label>
      <Files
        files={templateMedia}
        addFileFunction={() => undefined}
        deleteFileFunction={() => undefined}
        canAdd={false}
        canDelete={false}
      />
      {externalVideos.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <label style={{ fontSize: '0.85rem', color: '#475569' }}>
            {intl.formatMessage({
              id: 'externalVideoLinks',
              defaultMessage: 'External video links',
            })}
          </label>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {externalVideos.map((video, idx) => (
              <li
                key={`${video}-${idx}`}
                style={{
                  padding: '0.25rem 0',
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                <a
                  href={normalizeUrl(video)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ wordBreak: 'break-all' }}
                >
                  {video}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const description = stripHtml(parentTask?.description);
  const resource = parentTask?.resource || '';
  const instructionsAreEmpty =
    !description && !resource && templateMedia.length === 0 && externalVideos.length === 0;
  const instructionsTab = (
    <div>
      {parentLoading && !parentTask ? (
        <p style={{ color: '#64748b' }}>
          {intl.formatMessage({ id: 'loading', defaultMessage: 'Loading…' })}
        </p>
      ) : (
        <>
          {description && (
            <div className="form-group">
              <label>
                {intl.formatMessage({ id: 'description', defaultMessage: 'Description' })}
              </label>
              <div style={{ whiteSpace: 'pre-wrap' }}>{description}</div>
            </div>
          )}
          {resource && (
            <div className="form-group">
              <label>{intl.formatMessage({ id: 'resource', defaultMessage: 'Resource' })}</label>
              <div>
                <a href={normalizeUrl(resource)} target="_blank" rel="noopener noreferrer">
                  {resource}
                </a>
              </div>
            </div>
          )}
          {instructionsAreEmpty && (
            <p style={{ color: '#64748b' }}>
              {intl.formatMessage({
                id: 'noInstructions',
                defaultMessage: 'No instructions were configured for this task.',
              })}
            </p>
          )}
          {mediaSection}
        </>
      )}
    </div>
  );

  const { data: occurrenceFiles = [] } = useGetOccurrenceFilesQuery(occurrence.id, {
    skip: !occurrence.id,
  });
  const [archiveFile] = useArchiveOccurrenceFileMutation();

  const handleAddOccurrenceFile = async (formData: FormData): Promise<void> => {
    if (!selectedSiteId) return;
    try {
      await postTaskOccurrenceFile({
        options: { siteId: selectedSiteId },
        data: { occurrenceId: occurrence.id, file: formData },
      });
      dispatch(
        taskTemplatesApi.util.invalidateTags([
          { type: 'TaskTemplate' as const, id: `occurrence-files-${occurrence.id}` },
        ])
      );
    } catch (e: any) {
      apiError(e?.data || e?.message || 'Failed to upload file');
    }
  };
  const handleDeleteOccurrenceFile = async (file: any): Promise<void> => {
    try {
      await archiveFile({ occurrenceId: occurrence.id, fileId: file.id }).unwrap();
    } catch (e: any) {
      apiError(e?.data || 'Failed to remove file');
    }
  };

  const attachmentsTab = (
    <div>
      {canModifyFiles && (
        <div style={{ marginBottom: '0.75rem' }}>
          <UploadSection
            fileType={['image', 'video']}
            addFile={handleAddOccurrenceFile}
            multipleFiles
          />
        </div>
      )}
      {occurrenceFiles.length === 0 ? (
        <p style={{ color: '#64748b' }}>
          {intl.formatMessage({
            id: 'noOccurrenceFiles',
            defaultMessage: 'No files attached yet.',
          })}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {occurrenceFiles.map((file) => {
            const when = file.createdAt ? moment(file.createdAt).format('MMM D, h:mma') : '';
            const actor = file.creator || 'system';
            return (
              <li
                key={file.id}
                style={{
                  padding: '0.5rem 0',
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                  <a
                    href={file.downloadUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.9rem', fontWeight: 600, wordBreak: 'break-all' }}
                  >
                    {file.originalName || file.key}
                  </a>
                  <span style={{ color: '#64748b', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {when}
                  </span>
                </div>
                <div style={{ color: '#475569', fontSize: '0.85rem', marginTop: '0.15rem' }}>
                  {actor}
                </div>
                {file.description && (
                  <div
                    style={{
                      color: '#475569',
                      fontSize: '0.85rem',
                      marginTop: '0.25rem',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {file.description}
                  </div>
                )}
                {canModifyFiles && (
                  <div style={{ marginTop: '0.25rem' }}>
                    <button
                      className="button transparent"
                      type="button"
                      onClick={() => handleDeleteOccurrenceFile(file)}
                      style={{ padding: 0, fontSize: '0.8rem', color: '#64748b' }}
                    >
                      {intl.formatMessage({ id: 'remove', defaultMessage: 'Remove' })}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  const linkedTab =
    parentTask && hasLinks ? (
      <TaskTemplateLink task={parentTask} readOnly />
    ) : (
      <p style={{ color: '#64748b' }}>
        {intl.formatMessage({
          id: 'noLinkedTasks',
          defaultMessage: 'No linked tasks for this task.',
        })}
      </p>
    );

  const { data: historyEntries = [], isFetching: historyLoading } = useGetOccurrenceHistoryQuery(
    occurrence.id,
    { skip: !occurrence.id }
  );
  const { data: noteEntries = [], isFetching: notesLoading } = useGetOccurrenceNotesQuery(
    occurrence.id,
    { skip: !occurrence.id }
  );
  const [addNote, { isLoading: isAddingNote }] = useAddOccurrenceNoteMutation();
  const [deleteNote] = useDeleteOccurrenceNoteMutation();
  const [draftNote, setDraftNote] = useState<string>('');

  const formatEmployee = (
    e?: {
      firstName?: string | null;
      lastName?: string | null;
    } | null
  ): string => {
    if (!e) return '';
    const last = e.lastName || '';
    const first = e.firstName || '';
    return last && first ? `${last}, ${first}` : last || first || '';
  };

  const historyTab = (
    <div>
      {historyLoading && historyEntries.length === 0 ? (
        <p style={{ color: '#64748b' }}>
          {intl.formatMessage({ id: 'loading', defaultMessage: 'Loading…' })}
        </p>
      ) : historyEntries.length === 0 ? (
        <p style={{ color: '#64748b' }}>
          {intl.formatMessage({
            id: 'noOccurrenceHistory',
            defaultMessage: 'No history recorded yet.',
          })}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {historyEntries.map((entry) => {
            const actor = formatEmployee(entry.employee) || entry.creator || 'system';
            const when = entry.createdAt ? moment(entry.createdAt).format('MMM D, h:mma') : '';
            const NOTE_ADDED_PREFIX = 'Note added: ';
            const NOTE_REMOVED_PREFIX = 'Note removed: ';
            let transition: string;
            let displayReason: string | null = entry.reason ?? null;
            if (entry.reason?.startsWith(NOTE_ADDED_PREFIX)) {
              transition = intl.formatMessage({
                id: 'noteAdded',
                defaultMessage: 'Note added',
              });
              displayReason = entry.reason.slice(NOTE_ADDED_PREFIX.length);
            } else if (entry.reason?.startsWith(NOTE_REMOVED_PREFIX)) {
              transition = intl.formatMessage({
                id: 'noteRemoved',
                defaultMessage: 'Note removed',
              });
              displayReason = entry.reason.slice(NOTE_REMOVED_PREFIX.length);
            } else {
              transition = entry.fromStatus
                ? `${entry.fromStatus} → ${entry.toStatus}`
                : entry.toStatus;
            }
            return (
              <li
                key={entry.id}
                style={{
                  padding: '0.5rem 0',
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                  <strong style={{ fontSize: '0.9rem' }}>{transition}</strong>
                  <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{when}</span>
                </div>
                <div style={{ color: '#475569', fontSize: '0.85rem', marginTop: '0.15rem' }}>
                  {actor}
                </div>
                {displayReason && (
                  <div
                    style={{
                      color: '#475569',
                      fontSize: '0.85rem',
                      marginTop: '0.25rem',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {displayReason}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  const handleAddNote = async (): Promise<void> => {
    const text = draftNote.trim();
    if (!text) return;
    try {
      await addNote({ occurrenceId: occurrence.id, text }).unwrap();
      setDraftNote('');
    } catch (e: any) {
      apiError(e?.data || 'Failed to add note');
    }
  };
  const handleDeleteNote = async (noteId: number): Promise<void> => {
    try {
      await deleteNote({ occurrenceId: occurrence.id, noteId }).unwrap();
    } catch (e: any) {
      apiError(e?.data || 'Failed to remove note');
    }
  };

  const notesTab = (
    <div>
      <div className="form-group">
        <label>{intl.formatMessage({ id: 'addNote', defaultMessage: 'Add a note' })}</label>
        <textarea
          className="form-control"
          rows={2}
          value={draftNote}
          onChange={(e) => setDraftNote(e.target.value)}
          placeholder={intl.formatMessage({
            id: 'addNotePlaceholder',
            defaultMessage: 'Add context, hand-off info, or follow-ups…',
          })}
        />
        <div style={{ marginTop: '0.5rem' }}>
          <button
            className="button primary"
            type="button"
            onClick={handleAddNote}
            disabled={!draftNote.trim() || isAddingNote}
          >
            {isAddingNote
              ? intl.formatMessage({ id: 'saving', defaultMessage: 'Saving…' })
              : intl.formatMessage({ id: 'addNote', defaultMessage: 'Add note' })}
          </button>
        </div>
      </div>
      {notesLoading && noteEntries.length === 0 ? (
        <p style={{ color: '#64748b' }}>
          {intl.formatMessage({ id: 'loading', defaultMessage: 'Loading…' })}
        </p>
      ) : noteEntries.length === 0 ? (
        <p style={{ color: '#64748b' }}>
          {intl.formatMessage({
            id: 'noOccurrenceNotes',
            defaultMessage: 'No notes attached to this occurrence yet.',
          })}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {noteEntries.map((entry) => (
            <li
              key={entry.noteId}
              style={{
                padding: '0.5rem 0',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  marginBottom: '0.25rem',
                }}
              >
                <span style={{ color: '#475569', fontSize: '0.85rem' }}>
                  {entry.creator || 'system'} ·{' '}
                  {entry.createdAt ? moment(entry.createdAt).format('MMM D, h:mma') : ''}
                </span>
                <button
                  className="button"
                  type="button"
                  onClick={() => handleDeleteNote(entry.noteId)}
                  style={{ padding: '0 0.5rem', fontSize: '0.8rem' }}
                >
                  {intl.formatMessage({ id: 'remove', defaultMessage: 'Remove' })}
                </button>
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{entry.text}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const tabComponents = {
    Details: detailsTab,
    Instructions: instructionsTab,
    Attachments: attachmentsTab,
    'Linked Tasks': linkedTab,
    History: historyTab,
    Notes: notesTab,
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <h2 style={{ margin: 0 }}>
          {occurrence.task?.name || occurrence.name || `Occurrence #${occurrence.id}`}
        </h2>
        <button className="button" onClick={onClose} type="button">
          {intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <ContentTabs components={tabComponents} newStyle scrollable />

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button className="button primary" onClick={handleSave} disabled={isSaving} type="button">
          {isSaving
            ? intl.formatMessage({ id: 'saving', defaultMessage: 'Saving…' })
            : intl.formatMessage({ id: 'save', defaultMessage: 'Save' })}
        </button>
      </div>
    </div>
  );
};

export default OccurrenceDrawerBody;
